// utils/translateData.js
// Helper functions for translating dynamic data (products, categories, etc.)

import i18n from '../i18n/config';

/**
 * Get the current language code
 * @returns {string} Language code (fr, en, etc.)
 */
export const getCurrentLanguage = () => {
  return i18n.language || 'fr';
};

/**
 * Get translated field from an object
 * Supports multiple formats:
 * 1. Multilingual fields: { name_fr: "...", name_en: "..." }
 * 2. Nested multilingual: { name: { fr: "...", en: "..." } }
 * 3. Simple field: { name: "..." } (fallback)
 * 
 * @param {Object} obj - Object containing the data
 * @param {string} fieldName - Name of the field to get
 * @param {string} lang - Language code (optional, defaults to current language)
 * @returns {string} Translated value or fallback
 */
export const getTranslatedField = (obj, fieldName, lang = null) => {
  if (!obj) return '';
  
  const currentLang = lang || getCurrentLanguage();
  const fallbackLang = currentLang === 'fr' ? 'en' : 'fr';
  
  // Try format 1: name_fr, name_en
  const langSpecificField = `${fieldName}_${currentLang}`;
  if (obj[langSpecificField]) {
    return obj[langSpecificField];
  }
  
  // Try format 2: name: { fr: "...", en: "..." }
  if (obj[fieldName] && typeof obj[fieldName] === 'object') {
    if (obj[fieldName][currentLang]) {
      return obj[fieldName][currentLang];
    }
    if (obj[fieldName][fallbackLang]) {
      return obj[fieldName][fallbackLang];
    }
    // If it's an object but not a translation object, return empty
    return '';
  }
  
  // Try format 3: Simple field (fallback)
  if (obj[fieldName]) {
    return obj[fieldName];
  }
  
  // Try fallback language
  const fallbackField = `${fieldName}_${fallbackLang}`;
  if (obj[fallbackField]) {
    return obj[fallbackField];
  }
  
  return '';
};

/**
 * Get translated product name
 * @param {Object} product - Product object
 * @param {string} lang - Language code (optional)
 * @returns {string} Translated product name
 */
export const getTranslatedProductName = (product, lang = null) => {
  const translated = getTranslatedField(product, 'name', lang);
  if (translated) return translated;
  
  // Fallback to original helper
  return product?.name || product?.title || 'Product';
};

/**
 * Get translated product description
 * @param {Object} product - Product object
 * @param {string} lang - Language code (optional)
 * @returns {string} Translated product description
 */
export const getTranslatedProductDescription = (product, lang = null) => {
  return getTranslatedField(product, 'description', lang) || product?.description || '';
};

/**
 * Get translated category name
 * @param {Object} category - Category object
 * @param {string} lang - Language code (optional)
 * @returns {string} Translated category name
 */
export const getTranslatedCategoryName = (category, lang = null) => {
  const translated = getTranslatedField(category, 'name', lang);
  if (translated) return translated;
  
  return category?.name || 'Category';
};

/**
 * Get translated brand name
 * @param {Object} product - Product object
 * @param {string} lang - Language code (optional)
 * @returns {string} Translated brand name
 */
export const getTranslatedBrand = (product, lang = null) => {
  return getTranslatedField(product, 'brand', lang) || product?.brand || '';
};

/**
 * Translate an entire product object
 * Returns a new object with translated fields
 * @param {Object} product - Product object
 * @param {string} lang - Language code (optional)
 * @returns {Object} Product object with translated fields
 */
export const translateProduct = (product, lang = null) => {
  if (!product) return product;

  const currentLang = lang || getCurrentLanguage();
  const category = product.category;

  // Preserve all API fields (stock, availability, pricing) — only override translated text fields.
  return {
    ...product,
    id: product.id || product._id,
    _id: product._id || product.id,
    name: getTranslatedProductName(product, currentLang),
    title: product.title,
    description: getTranslatedProductDescription(product, currentLang),
    brand: getTranslatedBrand(product, currentLang),
    comparePrice:
      product.comparePrice ??
      product.compareAtPrice ??
      product.originalPrice ??
      product.oldPrice,
    category: category
      ? {
          ...category,
          id: category.id || category._id,
          _id: category._id || category.id,
          name: getTranslatedCategoryName(category, currentLang),
        }
      : category,
  };
};

/**
 * Translate an array of products
 * @param {Array} products - Array of product objects
 * @param {string} lang - Language code (optional)
 * @returns {Array} Array of translated product objects
 */
export const translateProducts = (products, lang = null) => {
  if (!Array.isArray(products)) return products;
  return products.map(product => translateProduct(product, lang));
};

/**
 * Translate a category object
 * @param {Object} category - Category object
 * @param {string} lang - Language code (optional)
 * @returns {Object} Translated category object
 */
export const translateCategory = (category, lang = null) => {
  if (!category) return category;
  
  const currentLang = lang || getCurrentLanguage();
  
  return {
    ...category,
    name: getTranslatedCategoryName(category, currentLang),
    description: getTranslatedField(category, 'description', currentLang) || category?.description || '',
  };
};

/**
 * Translate an array of categories
 * @param {Array} categories - Array of category objects
 * @param {string} lang - Language code (optional)
 * @returns {Array} Array of translated category objects
 */
export const translateCategories = (categories, lang = null) => {
  if (!Array.isArray(categories)) return categories;
  return categories.map(category => translateCategory(category, lang));
};

